import {invalidateOnSuccess, omneticApi} from '@dms/api/base';

import {
  CloseRecordAuditApiArg,
  CloseRecordAuditApiResponse,
  CreateRecordAuditNoteApiArg,
  CreateRecordAuditNoteApiResponse,
  CreateRecordAuditApiArg,
  CreateRecordAuditApiResponse,
  DeleteRecordAuditNoteApiArg,
  DeleteRecordAuditNoteApiResponse,
  DeleteRecordAuditApiArg,
  DeleteRecordAuditApiResponse,
  ExportRecordAuditPdfApiArg,
  ExportRecordAuditPdfApiResponse,
  GetRecordAuditNotesApiArg,
  GetRecordAuditNotesApiResponse,
  GetRecordAuditApiArg,
  GetRecordAuditApiResponse,
  ListRecordAuditsByRecordApiArg,
  ListRecordAuditsByRecordApiResponse,
  ReopenRecordAuditApiArg,
  ReopenRecordAuditApiResponse,
  SignRecordAuditApiArg,
  SignRecordAuditApiResponse,
  SubmitRecordAuditResultApiArg,
  SubmitRecordAuditResultApiResponse,
  UpdateRecordAuditHeaderApiArg,
  UpdateRecordAuditHeaderApiResponse,
} from './types';

// Out-of-order guard for rapid result clicks on the same task. Each submit gets a monotonic
// sequence number keyed by `${auditId}:${originalTaskDefinitionId}`; only the latest in-flight
// request for that task is allowed to merge its server response (or roll back on failure). A
// slower, older response can no longer clobber a newer answer — the newest request reconciles.
const resultRequestSequence = new Map<string, number>();

const nextResultSequence = (key: string) => {
  const sequence = (resultRequestSequence.get(key) ?? 0) + 1;
  resultRequestSequence.set(key, sequence);
  return sequence;
};

const isLatestResultSequence = (key: string, sequence: number) =>
  resultRequestSequence.get(key) === sequence;

// Tag marking the "audits paired to a record" collection, so create/delete refetch the
// per-record list without knowing which record(s) are currently cached.
const RECORD_AUDIT_LIST_TAG = {type: 'RecordAudit', id: 'RECORD_AUDIT_LIST'} as const;

const injectedRtkApi = omneticApi.injectEndpoints({
  endpoints: (build) => ({
    getRecordAudit: build.query<GetRecordAuditApiResponse, GetRecordAuditApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/record-audit/audit/${queryArg.auditId}`,
      }),
      providesTags: (result, error, queryArg) => [{type: 'RecordAudit', id: queryArg.auditId}],
    }),
    listRecordAuditsByRecord: build.query<
      ListRecordAuditsByRecordApiResponse,
      ListRecordAuditsByRecordApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/record-audit/audit`,
        params: {resourceId: queryArg.resourceId, recordId: queryArg.recordId},
      }),
      providesTags: (result) => [
        RECORD_AUDIT_LIST_TAG,
        ...(result ?? []).map((audit) => ({type: 'RecordAudit' as const, id: audit.id})),
      ],
    }),
    createRecordAudit: build.mutation<CreateRecordAuditApiResponse, CreateRecordAuditApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/record-audit/audit`,
        method: 'POST',
        body: queryArg.body,
      }),
      invalidatesTags: invalidateOnSuccess([RECORD_AUDIT_LIST_TAG]),
    }),
    submitRecordAuditResult: build.mutation<
      SubmitRecordAuditResultApiResponse,
      SubmitRecordAuditResultApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/record-audit/audit/${queryArg.auditId}/result/${queryArg.originalTaskDefinitionId}`,
        method: 'PATCH',
        body: queryArg.body,
      }),
      async onQueryStarted(queryArg, {dispatch, queryFulfilled}) {
        const {auditId, originalTaskDefinitionId, body} = queryArg;
        const sequenceKey = `${auditId}:${originalTaskDefinitionId}`;
        const sequence = nextResultSequence(sequenceKey);
        const patchResult = dispatch(
          injectedRtkApi.util.updateQueryData('getRecordAudit', {auditId}, (draft) => {
            const result = draft.taskResults.find(
              (r) => r.originalTaskDefinitionId === originalTaskDefinitionId
            );
            if (result) {
              if ('value' in body) {
                result.value = body.value ?? null;
              }
              if ('note' in body) {
                result.note = body.note ?? null;
              }
              if ('fileIds' in body) {
                // Removals can be applied optimistically by keeping only the
                // still-referenced files; additions lack metadata here and are
                // reconciled from the PATCH response. Mutating the cache also
                // lets patchResult.undo() revert a failed change.
                const fileIds = body.fileIds ?? [];
                result.files = result.files.filter((file) => fileIds.includes(file.id));
              }
            } else {
              draft.taskResults.push({
                originalTaskDefinitionId,
                value: body.value ?? null,
                note: body.note ?? null,
                files: [],
                updatedAt: new Date().toISOString(),
              });
            }
          })
        );
        try {
          // PATCH returns the updated task result + recomputed statistics — merge both into the
          // cached audit (no full-audit replace, no follow-up GET).
          const {data} = await queryFulfilled;
          // A newer click on the same task already superseded this request; skip the merge so this
          // stale response can't overwrite the newer answer (and its recomputed statistics).
          if (!isLatestResultSequence(sequenceKey, sequence)) {
            return;
          }
          dispatch(
            injectedRtkApi.util.updateQueryData('getRecordAudit', {auditId}, (draft) => {
              draft.statistics = data.statistics;
              const index = draft.taskResults.findIndex(
                (r) => r.originalTaskDefinitionId === originalTaskDefinitionId
              );
              if (index >= 0) {
                draft.taskResults[index] = data.taskResult;
              } else {
                draft.taskResults.push(data.taskResult);
              }
            })
          );
        } catch {
          // Only the latest request rolls back: undoing a stale failed request would revert to its
          // pre-change snapshot and wipe a newer in-flight answer. The newest request owns recovery.
          if (isLatestResultSequence(sequenceKey, sequence)) {
            patchResult.undo();
          }
        }
      },
    }),
    closeRecordAudit: build.mutation<CloseRecordAuditApiResponse, CloseRecordAuditApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/record-audit/audit/${queryArg.auditId}/close`,
        method: 'POST',
      }),
      invalidatesTags: invalidateOnSuccess((result, queryArg: CloseRecordAuditApiArg) => [
        {type: 'RecordAudit', id: queryArg.auditId},
      ]),
    }),
    reopenRecordAudit: build.mutation<ReopenRecordAuditApiResponse, ReopenRecordAuditApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/record-audit/audit/${queryArg.auditId}/reopen`,
        method: 'POST',
      }),
      invalidatesTags: invalidateOnSuccess((result, queryArg: ReopenRecordAuditApiArg) => [
        {type: 'RecordAudit', id: queryArg.auditId},
      ]),
    }),
    updateRecordAuditHeader: build.mutation<
      UpdateRecordAuditHeaderApiResponse,
      UpdateRecordAuditHeaderApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/record-audit/audit/${queryArg.auditId}/header`,
        method: 'PUT',
        body: queryArg.body,
      }),
      invalidatesTags: invalidateOnSuccess((result, queryArg: UpdateRecordAuditHeaderApiArg) => [
        {type: 'RecordAudit', id: queryArg.auditId},
      ]),
    }),
    deleteRecordAudit: build.mutation<DeleteRecordAuditApiResponse, DeleteRecordAuditApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/record-audit/audit/${queryArg.auditId}`,
        method: 'DELETE',
      }),
      invalidatesTags: invalidateOnSuccess((result, queryArg: DeleteRecordAuditApiArg) => [
        {type: 'RecordAudit', id: queryArg.auditId},
        RECORD_AUDIT_LIST_TAG,
      ]),
    }),
    signRecordAudit: build.mutation<SignRecordAuditApiResponse, SignRecordAuditApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/record-audit/audit/${queryArg.auditId}/sign`,
        method: 'POST',
        body: queryArg.body,
      }),
      invalidatesTags: invalidateOnSuccess((result, queryArg: SignRecordAuditApiArg) => [
        {type: 'RecordAudit', id: queryArg.auditId},
      ]),
    }),
    exportRecordAuditPdf: build.query<ExportRecordAuditPdfApiResponse, ExportRecordAuditPdfApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/record-audit/audit/${queryArg.auditId}/export/pdf`,
        responseHandler: async (response) => await response.blob(),
        cache: 'no-cache',
      }),
    }),
    // RecordAuditNote (reusable note templates) — T20-120660
    getRecordAuditNotes: build.query<GetRecordAuditNotesApiResponse, GetRecordAuditNotesApiArg>({
      query: () => ({
        url: `/dms/v1/record-audit/note`,
      }),
      providesTags: [{type: 'RecordAuditNote'}],
    }),
    createRecordAuditNote: build.mutation<
      CreateRecordAuditNoteApiResponse,
      CreateRecordAuditNoteApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/record-audit/note`,
        method: 'POST',
        body: queryArg.body,
      }),
      invalidatesTags: invalidateOnSuccess([{type: 'RecordAuditNote'}]),
    }),
    deleteRecordAuditNote: build.mutation<
      DeleteRecordAuditNoteApiResponse,
      DeleteRecordAuditNoteApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/record-audit/note/${queryArg.noteId}`,
        method: 'DELETE',
      }),
      invalidatesTags: invalidateOnSuccess([{type: 'RecordAuditNote'}]),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetRecordAuditQuery,
  useLazyGetRecordAuditQuery,
  useListRecordAuditsByRecordQuery,
  useCreateRecordAuditMutation,
  useSubmitRecordAuditResultMutation,
  useCloseRecordAuditMutation,
  useReopenRecordAuditMutation,
  useDeleteRecordAuditMutation,
  useUpdateRecordAuditHeaderMutation,
  useSignRecordAuditMutation,
  useLazyExportRecordAuditPdfQuery,
  useGetRecordAuditNotesQuery,
  useCreateRecordAuditNoteMutation,
  useDeleteRecordAuditNoteMutation,
} = injectedRtkApi;
